export type ThemeTokens = {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  border: string;
  input: string;
  ring: string;
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarBorder: string;
  sidebarRing: string;
};

export type Theme = {
  name: string;
  tagline: string;
  logo: string;
  light: ThemeTokens;
  dark: ThemeTokens;
};

const suntheanine: Theme = {
  name: 'Suntheanine',
  tagline: 'The Original L-Theanine',
  logo: '/logos/suntheanine.svg',
  light: {
    background: '#ffffff',
    foreground: '#0a1f3d',
    card: '#ffffff',
    cardForeground: '#0a1f3d',
    popover: '#ffffff',
    popoverForeground: '#0a1f3d',
    primary: '#1fa8e0',
    primaryForeground: '#ffffff',
    secondary: '#d6e5f2',
    secondaryForeground: '#0f47a0',
    muted: '#f0f4f9',
    mutedForeground: '#5a6c83',
    accent: '#d6e5f2',
    accentForeground: '#0f47a0',
    destructive: '#dc2626',
    border: '#d6e0ec',
    input: '#d6e0ec',
    ring: '#1fa8e0',
    chart1: '#1fa8e0',
    chart2: '#0f47a0',
    chart3: '#a6b6cc',
    chart4: '#5fc4ea',
    chart5: '#f5c842',
    sidebar: '#0f47a0',
    sidebarForeground: '#ffffff',
    sidebarPrimary: '#1fa8e0',
    sidebarPrimaryForeground: '#ffffff',
    sidebarAccent: 'rgba(255, 255, 255, 0.1)',
    sidebarAccentForeground: '#ffffff',
    sidebarBorder: 'rgba(255, 255, 255, 0.12)',
    sidebarRing: '#1fa8e0',
  },
  dark: {
    background: '#0a1f3d',
    foreground: '#e6eef9',
    card: '#0f2a4d',
    cardForeground: '#e6eef9',
    popover: '#0f2a4d',
    popoverForeground: '#e6eef9',
    primary: '#1fa8e0',
    primaryForeground: '#ffffff',
    secondary: '#173a6b',
    secondaryForeground: '#e6eef9',
    muted: '#173a6b',
    mutedForeground: '#a6b6cc',
    accent: '#173a6b',
    accentForeground: '#e6eef9',
    destructive: '#ef4444',
    border: 'rgba(255, 255, 255, 0.1)',
    input: 'rgba(255, 255, 255, 0.15)',
    ring: '#1fa8e0',
    chart1: '#1fa8e0',
    chart2: '#5fc4ea',
    chart3: '#a6b6cc',
    chart4: '#0f47a0',
    chart5: '#f5c842',
    sidebar: '#0a1f3d',
    sidebarForeground: '#e6eef9',
    sidebarPrimary: '#1fa8e0',
    sidebarPrimaryForeground: '#ffffff',
    sidebarAccent: 'rgba(255, 255, 255, 0.06)',
    sidebarAccentForeground: '#e6eef9',
    sidebarBorder: 'rgba(255, 255, 255, 0.1)',
    sidebarRing: '#1fa8e0',
  },
};

const taiyo: Theme = {
  name: 'Taiyo International',
  tagline: 'Intelligence Engine',
  logo: '/logos/taiyo.svg',
  light: {
    background: '#ffffff',
    foreground: '#1a1a1a',
    card: '#ffffff',
    cardForeground: '#1a1a1a',
    popover: '#ffffff',
    popoverForeground: '#1a1a1a',
    primary: '#1b3a5c',
    primaryForeground: '#ffffff',
    secondary: '#fff3e5',
    secondaryForeground: '#1b3a5c',
    muted: '#f1f4f8',
    mutedForeground: '#6b7280',
    accent: '#e8913a',
    accentForeground: '#ffffff',
    destructive: '#dc2626',
    border: '#e5e7eb',
    input: '#e5e7eb',
    ring: '#1b3a5c',
    chart1: '#1b3a5c',
    chart2: '#e8913a',
    chart3: '#4a6b8c',
    chart4: '#f5b97a',
    chart5: '#0a1f3d',
    sidebar: '#1b3a5c',
    sidebarForeground: '#ffffff',
    sidebarPrimary: '#e8913a',
    sidebarPrimaryForeground: '#ffffff',
    sidebarAccent: 'rgba(255, 255, 255, 0.1)',
    sidebarAccentForeground: '#ffffff',
    sidebarBorder: 'rgba(255, 255, 255, 0.12)',
    sidebarRing: '#e8913a',
  },
  dark: {
    background: '#0a1f3d',
    foreground: '#e6eef9',
    card: '#0f2a4d',
    cardForeground: '#e6eef9',
    popover: '#0f2a4d',
    popoverForeground: '#e6eef9',
    primary: '#e8913a',
    primaryForeground: '#1a1a1a',
    secondary: '#1b3a5c',
    secondaryForeground: '#e6eef9',
    muted: '#173a6b',
    mutedForeground: '#a6b6cc',
    accent: '#e8913a',
    accentForeground: '#1a1a1a',
    destructive: '#ef4444',
    border: 'rgba(255, 255, 255, 0.1)',
    input: 'rgba(255, 255, 255, 0.15)',
    ring: '#e8913a',
    chart1: '#e8913a',
    chart2: '#1b3a5c',
    chart3: '#f5b97a',
    chart4: '#4a6b8c',
    chart5: '#a6b6cc',
    sidebar: '#0a1f3d',
    sidebarForeground: '#e6eef9',
    sidebarPrimary: '#e8913a',
    sidebarPrimaryForeground: '#1a1a1a',
    sidebarAccent: 'rgba(255, 255, 255, 0.06)',
    sidebarAccentForeground: '#e6eef9',
    sidebarBorder: 'rgba(255, 255, 255, 0.1)',
    sidebarRing: '#e8913a',
  },
};

export const themes = { suntheanine, taiyo } as const;
export type ThemeKey = keyof typeof themes;

export function getTheme(hostname: string): Theme {
  if (hostname.includes('suntheanine')) return themes.suntheanine;
  return themes.taiyo;
}

const TOKEN_TO_CSS_VAR: Record<keyof ThemeTokens, string> = {
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  cardForeground: '--card-foreground',
  popover: '--popover',
  popoverForeground: '--popover-foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  destructive: '--destructive',
  border: '--border',
  input: '--input',
  ring: '--ring',
  chart1: '--chart-1',
  chart2: '--chart-2',
  chart3: '--chart-3',
  chart4: '--chart-4',
  chart5: '--chart-5',
  sidebar: '--sidebar',
  sidebarForeground: '--sidebar-foreground',
  sidebarPrimary: '--sidebar-primary',
  sidebarPrimaryForeground: '--sidebar-primary-foreground',
  sidebarAccent: '--sidebar-accent',
  sidebarAccentForeground: '--sidebar-accent-foreground',
  sidebarBorder: '--sidebar-border',
  sidebarRing: '--sidebar-ring',
};

function tokensToCssRules(tokens: ThemeTokens): string {
  return (Object.entries(tokens) as [keyof ThemeTokens, string][])
    .map(([key, value]) => `${TOKEN_TO_CSS_VAR[key]}: ${value};`)
    .join(' ');
}

export function themeToCss(theme: Theme): string {
  return `:root { ${tokensToCssRules(theme.light)} } .dark { ${tokensToCssRules(theme.dark)} }`;
}
